/**
 * Source-of-truth unit catalog for core.
 *
 * Invariants:
 * - Each unit maps to exactly one base unit and type.
 * - SI prefixes are opt-in per base key and expand deterministically.
 *
 * @see `packages/core/docs/namespace.md`
 */
export const UNIT_TYPES = [
  "length",
  "mass",
  "volume",
  "energy",
  "temperature",
  "time",
  "pharmacology",
  "scalar",
] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

type CanonicalUnitName =
  | "gram"
  | "meter"
  | "liter"
  | "second"
  | "calorie"
  | "celsius"
  | "IU"
  | "count"
  | "percent";

export type UnitDefinition = {
  baseUnit: {
    name: CanonicalUnitName;
    type: UnitType;
  };
  multiplier: number;
  siPrefixes?: SIPrefix[];
};

export const SI_PREFIX_MULTIPLIERS = {
  k: 1000,
  d: 0.1,
  c: 0.01,
  m: 0.001,
  mc: 0.000001,
} as const satisfies Record<string, number>;

type SIPrefix = keyof typeof SI_PREFIX_MULTIPLIERS;

export const UNIT_DEFINITIONS = {
  g: {
    baseUnit: { name: "gram", type: "mass" },
    multiplier: 1,
    siPrefixes: ["k", "m", "mc"],
  },
  oz: { baseUnit: { name: "gram", type: "mass" }, multiplier: 28.3495 },
  lb: { baseUnit: { name: "gram", type: "mass" }, multiplier: 453.592 },
  st: { baseUnit: { name: "gram", type: "mass" }, multiplier: 6350.29 },
  m: {
    baseUnit: { name: "meter", type: "length" },
    multiplier: 1,
    siPrefixes: ["k", "c", "m"],
  },
  in: { baseUnit: { name: "meter", type: "length" }, multiplier: 0.0254 },
  ft: { baseUnit: { name: "meter", type: "length" }, multiplier: 0.3048 },
  yd: { baseUnit: { name: "meter", type: "length" }, multiplier: 0.9144 },
  mi: { baseUnit: { name: "meter", type: "length" }, multiplier: 1609.34 },
  L: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 1,
    siPrefixes: ["m", "mc"],
  },
  fl_oz_us: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 0.0295735,
  },
  fl_oz_imp: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 0.0284131,
  },
  pt_us: { baseUnit: { name: "liter", type: "volume" }, multiplier: 0.473176 },
  pt_imp: { baseUnit: { name: "liter", type: "volume" }, multiplier: 0.568261 },
  cup_us: { baseUnit: { name: "liter", type: "volume" }, multiplier: 0.24 },
  cup_imp: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 0.284131,
  },
  tbsp_us: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 1 / 67.628,
  },
  tbsp_imp: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 1 / 56.312,
  },
  tsp_us: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 1 / 202.9,
  },
  tsp_imp: {
    baseUnit: { name: "liter", type: "volume" },
    multiplier: 1 / 168.9,
  },
  s: {
    baseUnit: { name: "second", type: "time" },
    multiplier: 1,
    siPrefixes: ["m", "mc"],
  },
  min: { baseUnit: { name: "second", type: "time" }, multiplier: 60 },
  hr: { baseUnit: { name: "second", type: "time" }, multiplier: 3600 },
  d: { baseUnit: { name: "second", type: "time" }, multiplier: 86400 },
  cal: { baseUnit: { name: "calorie", type: "energy" }, multiplier: 1 },
  kcal: { baseUnit: { name: "calorie", type: "energy" }, multiplier: 1000 },
  J: { baseUnit: { name: "calorie", type: "energy" }, multiplier: 0.239006 },
  kJ: { baseUnit: { name: "calorie", type: "energy" }, multiplier: 239.006 },
  degC: { baseUnit: { name: "celsius", type: "temperature" }, multiplier: 1 },
  degF: {
    baseUnit: { name: "celsius", type: "temperature" },
    multiplier: 5 / 9,
  },
  K: { baseUnit: { name: "celsius", type: "temperature" }, multiplier: 1 },
  IU: { baseUnit: { name: "IU", type: "pharmacology" }, multiplier: 1 },
  sp_gr: { baseUnit: { name: "count", type: "scalar" }, multiplier: 1 },
  count: { baseUnit: { name: "count", type: "scalar" }, multiplier: 1 },
  "%": { baseUnit: { name: "percent", type: "scalar" }, multiplier: 1 },
} as const satisfies Record<string, UnitDefinition>;
